/**
 * The bar along the bottom of a score: where the music is, and how fast and how big it is.
 *
 * One strip holds the transport, the speed and the zoom because that is where a musician
 * looks for them, and because the alternative -- a row of labelled buttons above the staves
 * -- pushes the music down the screen to make room for words the icons already say.
 *
 * The playhead is a slider and not a readout. Being able to drag to the bar you want is most
 * of what an audition is for: nobody listening to a re-scored passage wants to hear it from
 * the top every time, and `seek` moves the position without starting or stopping, so
 * dragging while paused stays paused.
 *
 * Speed multiplies the score's own tempo marks and changes nothing that is printed or saved;
 * the panel that owns this bar does the multiplying and says so.
 *
 * The pager is here too, since the desk holds one sheet at a time and this bar never scrolls
 * away from it. So is full screen, last, where every player puts it: it takes this bar with
 * the page, so the music can still be stopped, sped up and turned while it is watched.
 */

import { ZoomControl } from "./ScoreCanvas";
import type { Zoom } from "./ScoreCanvas";
import ScorePager from "./ScorePager";
import type { Fullscreen } from "../lib/use-fullscreen";

export interface ScoreTransportProps {
  readonly playing: boolean;
  /**
   * The instruments are still being fetched, and a press of play is waiting for them.
   *
   * The button stays live rather than going grey, because the press is not refused: it is
   * remembered and acted on the moment the sound is there. Pulsing says "heard you" without
   * taking the control away, which a disabled button would.
   */
  readonly loading: boolean;
  /** Seconds into the piece, and how many it lasts. */
  readonly position: number;
  readonly duration: number;
  onToggle(): void;
  onStop(): void;
  onSeek(seconds: number): void;
  /** Multiples of the speed the score is marked at. */
  readonly rate: number;
  onRate(rate: number): void;
  /** Quarter notes per minute the score itself is marked at, where it is marked at all. */
  readonly tempo: number | null;
  readonly zoom: Zoom;
  readonly resolvedZoom: number;
  onZoom(zoom: Zoom): void;
  /** The sheet on the desk, 1-based, and how many there are. */
  readonly page: number;
  readonly pages: number;
  onPage(page: number): void;
  /** The desk's full screen, where it has one. */
  readonly fullscreen?: Fullscreen;
}

/** The speeds offered, as multiples of what the score is marked at. */
export const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

/** Minutes and seconds, the only unit a transport is read in. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

function Icon({ path, filled }: { path: string; filled?: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-[15px] w-[15px]" aria-hidden="true">
      <path
        d={path}
        fill={filled === true ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth={filled === true ? 1 : 1.6}
        strokeLinejoin="round"
      />
    </svg>
  );
}

const PLAY = "M4.5 3 13 8l-8.5 5z";
const PAUSE = "M5 3.5h2.2v9H5z M8.8 3.5H11v9H8.8z";
const STOP = "M4 4h8v8H4z";
const ENTER = "M2.5 6V2.5H6 M10 2.5h3.5V6 M13.5 10v3.5H10 M6 13.5H2.5V10";
const LEAVE = "M6 2.5V6H2.5 M13.5 6H10V2.5 M10 13.5V10h3.5 M2.5 10H6v3.5";

export default function ScoreTransport({
  playing,
  loading,
  position,
  duration,
  onToggle,
  onStop,
  onSeek,
  rate,
  onRate,
  tempo,
  zoom,
  resolvedZoom,
  onZoom,
  page,
  pages,
  onPage,
  fullscreen,
}: ScoreTransportProps): JSX.Element {
  const at = Math.min(Math.max(position, 0), duration);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-shell-edge bg-shell-bar px-2.5 py-1.5">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onToggle}
          aria-label={loading ? "Loading instruments" : playing ? "Pause" : "Play"}
          aria-busy={loading}
          title={loading ? "Loading the instruments this score is scored for" : undefined}
          className={`flex h-8 w-8 items-center justify-center rounded bg-shell-live text-white hover:brightness-110${
            loading ? " animate-pulse" : ""
          }`}
        >
          <Icon path={playing ? PAUSE : PLAY} filled />
        </button>
        <button
          type="button"
          onClick={onStop}
          aria-label="Stop"
          className="flex h-8 w-8 items-center justify-center rounded text-shell-text hover:bg-shell-hot"
        >
          <Icon path={STOP} filled />
        </button>
      </div>

      <div className="flex min-w-[180px] flex-1 items-center gap-2">
        <span className="font-mono text-[11px] tabular-nums text-shell-lit">{clock(at)}</span>
        <input
          type="range"
          min={0}
          max={duration || 1}
          step={0.05}
          value={at}
          onChange={(event) => onSeek(Number(event.target.value))}
          aria-label="Position"
          className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-shell-key accent-shell-live"
        />
        <span className="font-mono text-[11px] tabular-nums text-shell-dim">{clock(duration)}</span>
      </div>

      {/* Wraps on a phone rather than running off the desk, which clips it; the seams go
          there, since a wrapped line would start with one. */}
      <div className="flex flex-wrap items-center gap-2">
        {tempo === null ? null : (
          <span
            className="font-mono text-[11px] text-shell-dim"
            title="The tempo the score is marked at, carried from the piece it was made from"
          >
            &#9833; = {Math.round(tempo)}
          </span>
        )}
        <label className="flex items-center gap-1 text-[11px] text-shell-dim">
          Speed
          <select
            value={rate}
            onChange={(event) => onRate(Number(event.target.value))}
            className="h-7 rounded bg-shell-key px-1.5 font-mono text-[11px] text-shell-lit outline-none hover:bg-shell-hot"
          >
            {RATES.map((one) => (
              <option key={one} value={one}>
                {one}&times;
              </option>
            ))}
          </select>
        </label>
        <span aria-hidden="true" className="hidden h-4 w-px bg-shell-seam sm:block" />
        <ScorePager page={page} count={pages} onPage={onPage} look="shell" />
        <span aria-hidden="true" className="hidden h-4 w-px bg-shell-seam sm:block" />
        <ZoomControl zoom={zoom} resolved={resolvedZoom} onZoom={onZoom} />
        {fullscreen === undefined ? null : (
          <button
            type="button"
            onClick={(event) => {
              // Let go of the focus, or the space bar would press this again and leave.
              event.currentTarget.blur();
              fullscreen.toggle();
            }}
            aria-label={fullscreen.on ? "Leave full screen" : "Full screen"}
            title={fullscreen.on ? "Leave full screen (Esc)" : "Full screen: the page and this bar"}
            className="flex h-7 w-7 items-center justify-center rounded text-shell-text hover:bg-shell-hot"
          >
            <Icon path={fullscreen.on ? LEAVE : ENTER} />
          </button>
        )}
      </div>
    </div>
  );
}
